import { useCallback, useEffect, useState } from "react";
import { FiPlus, FiRefreshCw, FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";

import { PageContainer } from "../../components/PageComponents";
import {
  createReferralCode,
  fetchReferralCodes,
  getReferralApiError,
  searchReferralUsers,
  updateReferralCode,
} from "../../features/auth/api/referralCodes";
import { useAppSelector } from "../../hooks/hooks";
import { ReferralCodeModal, ReferralCodesTable } from "./components";
import {
  REFERRAL_CODE_STATUS_OPTIONS,
  ReferralCode,
  ReferralCodePagination,
  ReferralCodePayload,
  ReferralCodeStatus,
  ReferralUserOption,
} from "./types";

const emptyPagination: ReferralCodePagination = {
  data: [],
  current_page: 1,
  last_page: 1,
  per_page: 15,
  total: 0,
};

const Referrals = () => {
  const token = useAppSelector((state) => state.auth.token);
  const [pagination, setPagination] =
    useState<ReferralCodePagination>(emptyPagination);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    ReferralCodeStatus | ""
  >("");
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState<ReferralUserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const loadReferralCodes = useCallback(async () => {
    if (!token) {
      setError("Your session has expired. Please sign in again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchReferralCodes(token, {
        search: debouncedSearch,
        status: statusFilter,
        page,
        per_page: 15,
      });
      setPagination(result);
    } catch (requestError) {
      setError(
        getReferralApiError(
          requestError,
          "Unable to load referral codes. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, statusFilter, token]);

  useEffect(() => {
    loadReferralCodes();
  }, [loadReferralCodes]);

  useEffect(() => {
    if (!modalOpen || !token) return;

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setUsersLoading(true);
      try {
        const result = await searchReferralUsers(token, userSearch);
        if (!cancelled) setUsers(result);
      } catch (requestError) {
        if (!cancelled) {
          setUsers([]);
          toast.error(
            getReferralApiError(requestError, "Unable to search users."),
          );
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [modalOpen, token, userSearch]);

  const openCreateModal = () => {
    setUserSearch("");
    setUsers([]);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setUserSearch("");
  };

  const handleSubmit = async (payload: ReferralCodePayload) => {
    if (!token) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setSubmitting(true);
    try {
      await createReferralCode(token, payload);
      toast.success("Referral code created successfully.");

      setModalOpen(false);
      setUserSearch("");
      await loadReferralCodes();
    } catch (requestError) {
      toast.error(
        getReferralApiError(requestError, "Unable to save referral code."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (
    referralCode: ReferralCode,
    nextStatus: ReferralCodeStatus,
  ) => {
    if (!token || updatingId !== null) return;
    if (referralCode.status === nextStatus) return;

    if (
      nextStatus === "deleted" &&
      !window.confirm(
        `Delete referral code ${referralCode.code}? This permanently invalidates every existing referral attached to the code. The owner cannot receive a replacement code, and this action cannot be undone.`,
      )
    ) {
      return;
    }

    setUpdatingId(referralCode.id);

    try {
      await updateReferralCode(token, referralCode.id, nextStatus);
      const successMessages: Record<ReferralCodeStatus, string> = {
        active: "Referral code activated. New signups and rewards are enabled.",
        closed:
          "Referral code closed. Existing referrals remain eligible for rewards.",
        deleted:
          "Referral code deleted. Existing referrals have been invalidated.",
      };
      toast.success(successMessages[nextStatus]);
      await loadReferralCodes();
    } catch (requestError) {
      toast.error(
        getReferralApiError(
          requestError,
          "Unable to update referral code status.",
        ),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <PageContainer title="Referrals">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200">
        Assign referral codes to users and manage whether they accept new
        signups or earn DG Points from existing referrals.
        <dl className="mt-3 grid gap-3 md:grid-cols-3">
          {REFERRAL_CODE_STATUS_OPTIONS.map((option) => (
            <div
              key={option.value}
              className="rounded-lg border border-blue-200/70 bg-white/60 p-3 dark:border-blue-800/70 dark:bg-zinc-950/30"
            >
              <dt className="font-bold">{option.label}</dt>
              <dd className="mt-1 text-xs leading-5">{option.description}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="grid gap-4 rounded-xl border border-gray-200 p-4 dark:border-zinc-800 sm:grid-cols-2 lg:grid-cols-[minmax(260px,1fr)_220px_auto]">
        <label className="relative block">
          <span className="sr-only">Search referral codes</span>
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by code or user"
            className="h-12 w-full rounded-full border border-gray-200 bg-gray-50 pl-11 pr-4 text-gray-900 outline-none focus:border-pry-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          />
        </label>

        <label>
          <span className="sr-only">Filter by referral code status</span>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(
                event.target.value as ReferralCodeStatus | "",
              );
              setPage(1);
            }}
            className="h-12 w-full rounded-full border border-gray-200 bg-gray-50 px-4 text-gray-700 outline-none focus:border-pry-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          >
            <option value="">All statuses</option>
            {REFERRAL_CODE_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-pry-500 to-pry-600 px-5 font-semibold text-white"
        >
          <FiPlus aria-hidden="true" />
          Create referral code
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Referral codes
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {pagination.total} code{pagination.total === 1 ? "" : "s"} found
          </p>
        </div>
        <button
          type="button"
          onClick={loadReferralCodes}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60 dark:border-zinc-700 dark:text-gray-200"
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          <span>{error}</span>
          <button
            type="button"
            onClick={loadReferralCodes}
            className="font-semibold underline"
          >
            Try again
          </button>
        </div>
      ) : null}

      <ReferralCodesTable
        referralCodes={pagination.data}
        loading={loading}
        updatingId={updatingId}
        onStatusChange={handleStatusChange}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500 dark:text-gray-400">
        <span>
          Page {pagination.current_page} of {pagination.last_page}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={loading || pagination.current_page <= 1}
            className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:text-gray-200"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() =>
              setPage((current) =>
                Math.min(pagination.last_page, current + 1),
              )
            }
            disabled={
              loading || pagination.current_page >= pagination.last_page
            }
            className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:text-gray-200"
          >
            Next
          </button>
        </div>
      </div>

      <ReferralCodeModal
        isOpen={modalOpen}
        users={users}
        usersLoading={usersLoading}
        userSearch={userSearch}
        isSubmitting={submitting}
        onUserSearchChange={setUserSearch}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </PageContainer>
  );
};

export default Referrals;
